import { useState } from "react";

interface TooltipState {
    show: boolean;
    x: number;
    y: number;
    type: 'real' | 'goal';
    value: number;
    week: number;
}

export const useWeightChart = (actualWeights: number[], goalWeights: number[]) => {
    const [tooltip, setTooltip] = useState<TooltipState>({
        show: false,
        x: 0,
        y: 0,
        type: 'real',
        value: 0,
        week: 1
    });

    // Use all data points for both mobile and desktop
    const filteredActualWeights = actualWeights;
    const filteredGoalWeights = goalWeights.slice(0, actualWeights.length);

    const allWeights = [...filteredActualWeights, ...filteredGoalWeights];
    const maxWeight = Math.max(...allWeights);
    const minWeight = Math.min(...allWeights);
    
    // Expand range by 2 units below min and 2 above max
    const expandedMin = Math.floor(minWeight - 2);
    const expandedMax = Math.ceil(maxWeight + 2);
    const weightRange = expandedMax - expandedMin;
    
    const chartHeight = 200;
    const chartWidth = 400;
    const padding = 20; // Padding for dots

    // Helper function to convert weight to Y coordinate
    const weightToY = (weight: number) => {
        return padding + (chartHeight - 2 * padding) - ((weight - expandedMin) / weightRange) * (chartHeight - 2 * padding);
    };

    // Helper function to get Y-axis label values
    const getYAxisLabels = () => {
        return [expandedMax, expandedMin + weightRange * 0.75, expandedMin + weightRange * 0.5, expandedMin + weightRange * 0.25, expandedMin];
    };

    // Helper function to convert week index to X coordinate
    const weekToX = (index: number, totalPoints: number) => {
        if (totalPoints === 1) {
            return padding; // Position completely to the left with just padding
        }
        return padding + (index / (totalPoints - 1)) * (chartWidth - 2 * padding);
    };

    // Generate path for goal line
    const goalPath = filteredGoalWeights.map((weight, index) => {
        const x = weekToX(index, filteredGoalWeights.length);
        const y = weightToY(weight);
        return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ');

    // Generate path for actual line
    const actualPath = filteredActualWeights.map((weight, index) => {
        const x = weekToX(index, filteredActualWeights.length);
        const y = weightToY(weight);
        return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ');

    const handlePointHover = (event: React.MouseEvent, type: 'real' | 'goal', value: number, week: number) => {
        const x = weekToX(week - 1, type === 'real' ? filteredActualWeights.length : filteredGoalWeights.length);
        const y = weightToY(value);
        setTooltip({
            show: true,
            x: x,
            y: y - 15, // Position closer above the dot
            type,
            value,
            week
        });
    };

    const handlePointLeave = () => {
        setTooltip(prev => ({ ...prev, show: false }));
    };

    const xLabelPadding = padding + 13;

    // Responsive padding for mobile
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const responsiveXLabelPadding = isMobile ? padding - 8 : xLabelPadding;

    return {
        // Data
        filteredActualWeights,
        filteredGoalWeights,
        allWeights,
        
        // Chart dimensions
        chartHeight,
        chartWidth,
        padding,
        weightRange,
        expandedMin,
        expandedMax,
        
        // Functions
        weightToY,
        weekToX,
        getYAxisLabels,
        goalPath,
        actualPath,
        
        // Tooltip
        tooltip,
        handlePointHover,
        handlePointLeave,
        
        // Responsive
        responsiveXLabelPadding,
        isMobile
    };
}; 